import { useEffect, useState } from 'react';
import { IntegrityEventType } from 'generated/graphql';
import {
  StartIntegritySession,
  useIntegrityMonitoring,
} from 'hooks/useIntegrityMonitoring';
import { useVisionIntegrityDetection } from 'hooks/useVisionIntegrityDetection';

type HookProps = {
  // The project's integrity-monitoring toggle; nothing runs when off.
  enabled: boolean;
  // Camera feed from useIntegrityCamera. Monitoring starts the moment access
  // is first granted (still on the intro page, per the disclaimer shown
  // there) and stays on until the flow unmounts.
  stream: MediaStream | null;
  // Owner-specific start mutation (cognitive vs personality action).
  startSession: StartIntegritySession;
};

// Online-assessment wrapper around the integrity monitoring + vision cores.
// Unlike the AI interview there is no LiveKit room: the camera stream comes
// from getUserMedia, and losing it mid-test is reported as CAMERA_TURNED_OFF
// (held open until it comes back) rather than ending the session.
export function useOnlineAssessmentIntegrityMonitoring({
  enabled,
  stream,
  startSession,
}: HookProps): void {
  // Latched: once the camera was granted, monitoring stays active even if
  // the stream is later revoked — the revocation itself is evidence.
  const [everGranted, setEverGranted] = useState(false);
  useEffect(() => {
    if (stream) setEverGranted(true);
  }, [stream]);

  const active = enabled && everGranted;

  const { reportEvent, openEvent, closeEvent } = useIntegrityMonitoring({
    enabled: active,
    startSession,
  });

  useEffect(() => {
    if (!active) return;
    if (!stream) {
      openEvent(IntegrityEventType.CameraTurnedOff);
    } else {
      closeEvent(IntegrityEventType.CameraTurnedOff);
    }
  }, [active, stream, openEvent, closeEvent]);

  useVisionIntegrityDetection({
    enabled: active,
    stream,
    openEvent,
    closeEvent,
    reportEvent,
  });
}
